import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SidebarAdmin from "./Utils/SidebarAdmin";
import { fetchForeignResource } from "../apis/resources";
import { logout } from "../apis/backend";
import {
  createAttendanceSession,
  startAttendanceSession,
  closeAttendanceSession,
  getDisplayData,
  getSessionRecords,
  markManualAttendance,
  getAllSessionsHistory,
} from "../apis/attendanceApi";
import {
  QrCode,
  Play,
  Square,
  PlusCircle,
  Users,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  UserCheck,
  FileSpreadsheet,
  MapPin,
  Key,
  History,
  Eye,
  Search,
  LogOut,
} from "lucide-react";
import "./Upload.css";

export default function AttendanceManagement() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [programs, setPrograms] = useState<any[]>([]);
  const [campusLocations, setCampusLocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

  // Form State for Session Creation
  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [selectedCampusLocationId, setSelectedCampusLocationId] = useState("");
  const [sessionLabel, setSessionLabel] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledStartTime, setScheduledStartTime] = useState("");

  // Active Managed Session
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<any | null>(null);
  const [displayData, setDisplayData] = useState<any | null>(null);
  const [records, setRecords] = useState<any[]>([]);

  // History Sessions State
  const [historySessions, setHistorySessions] = useState<any[]>([]);
  const [selectedHistorySessionId, setSelectedHistorySessionId] = useState<string | null>(null);
  const [historySearchQuery, setHistorySearchQuery] = useState("");

  // Manual Attendance Form
  const [enrolledStudents, setEnrolledStudents] = useState<any[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [manualReason, setManualReason] = useState("QR scanner camera failed");

  // Timer reference for 10s QR/OTP refresh
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadInitialData();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [progData, locData] = await Promise.all([
        fetchForeignResource("Program").catch(() => []),
        fetchForeignResource("CampusLocation").catch(() => []),
      ]);
      setPrograms(Array.isArray(progData) ? progData : progData?.resource || []);
      setCampusLocations(Array.isArray(locData) ? locData : locData?.resource || []);
      await fetchHistorySessions();
    } catch (err: any) {
      console.error(err);
      setMessage({ text: err.message || "Failed to load programs/locations", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchHistorySessions = async () => {
    try {
      const res = await getAllSessionsHistory(0, 100);
      setHistorySessions(res.sessions || []);
    } catch (err) {
      console.error("Failed to load history sessions:", err);
    }
  };

  // Filter campus locations based on selected program campus_id (if present)
  const filteredCampusLocations = campusLocations.filter((loc) => {
    if (!selectedProgramId) return true;
    const prog = programs.find((p) => p.id === selectedProgramId);
    const progCampus = prog?.extra_data?.campus_id;
    if (!progCampus) return true;
    return loc.campus_id === progCampus;
  });

  const handleCreateSession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProgramId || !selectedCampusLocationId || !sessionLabel) {
      setMessage({ text: "Please fill all required fields", type: "error" });
      return;
    }

    try {
      const res = await createAttendanceSession({
        program_id: selectedProgramId,
        campus_location_id: selectedCampusLocationId,
        session_label: sessionLabel,
        scheduled_date: scheduledDate,
        scheduled_start_time: scheduledStartTime,
      });

      setActiveSessionId(res.session_id);
      setSelectedHistorySessionId(res.session_id);
      setSessionData({
        session_id: res.session_id,
        status: "SCHEDULED",
        session_label: sessionLabel,
        program_id: selectedProgramId,
      });
      setMessage({ text: "Attendance Session created in SCHEDULED state. Click 'Start Attendance' to enable QR/OTP scanning.", type: "success" });
      loadEnrolledStudents(selectedProgramId);
      fetchHistorySessions();
    } catch (err: any) {
      setMessage({ text: err.message || "Failed to create session", type: "error" });
    }
  };

  const handleStartSession = async (sessionId?: string) => {
    const targetId = sessionId || activeSessionId;
    if (!targetId) return;
    try {
      await startAttendanceSession(targetId);
      setActiveSessionId(targetId);
      setSelectedHistorySessionId(targetId);
      setSessionData((prev: any) => ({ ...(prev || {}), session_id: targetId, status: "ACTIVE" }));
      setMessage({ text: "Attendance Session is now ACTIVE! QR and 6-Digit OTP are enabled.", type: "success" });
      startQrRefreshLoop(targetId);
      fetchHistorySessions();
    } catch (err: any) {
      setMessage({ text: err.message || "Failed to start session", type: "error" });
    }
  };

  const handleCloseSession = async (sessionId?: string) => {
    const targetId = sessionId || activeSessionId;
    if (!targetId) return;
    try {
      await closeAttendanceSession(targetId);
      setSessionData((prev: any) => (prev ? { ...prev, status: "CLOSED" } : null));
      setDisplayData(null);
      if (timerRef.current) clearInterval(timerRef.current);
      setMessage({ text: "Attendance Session is now CLOSED. QR code and OTP invalidated.", type: "success" });
      fetchRecords(targetId);
      fetchHistorySessions();
    } catch (err: any) {
      setMessage({ text: err.message || "Failed to close session", type: "error" });
    }
  };

  const startQrRefreshLoop = (sessionId: string) => {
    if (timerRef.current) clearInterval(timerRef.current);
    refreshDisplay(sessionId);

    timerRef.current = setInterval(() => {
      refreshDisplay(sessionId);
    }, 10000);
  };

  const refreshDisplay = async (sessionId: string) => {
    try {
      const data = await getDisplayData(sessionId);
      setDisplayData(data);
      fetchRecords(sessionId);
    } catch (err: any) {
      console.error(err);
    }
  };

  const fetchRecords = async (sessionId: string) => {
    try {
      const res = await getSessionRecords(sessionId, 0, 100);
      setRecords(res.records || []);
    } catch (err: any) {
      console.error(err);
    }
  };

  const loadEnrolledStudents = async (programId: string) => {
    try {
      const [regsData, studData] = await Promise.all([
        fetchForeignResource("program_registration").catch(() => []),
        fetchForeignResource("student").catch(() => []),
      ]);

      const regs = Array.isArray(regsData) ? regsData : regsData?.resource || [];
      const studs = Array.isArray(studData) ? studData : studData?.resource || [];

      const enrolledIds = regs.filter((r: any) => r.program_id === programId).map((r: any) => r.student_id);
      const filteredStuds = studs.filter((s: any) => enrolledIds.includes(s.id));
      setEnrolledStudents(filteredStuds);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSelectHistorySession = (session: any) => {
    setSelectedHistorySessionId(session.id);
    fetchRecords(session.id);
    if (session.program_id) {
      loadEnrolledStudents(session.program_id);
    }
    if (session.status === "ACTIVE") {
      setActiveSessionId(session.id);
      setSessionData(session);
      startQrRefreshLoop(session.id);
    }
  };

  const handleManualMarkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const targetSessionId = activeSessionId || selectedHistorySessionId;
    if (!targetSessionId || !selectedStudentId) {
      setMessage({ text: "Select a student to mark manual attendance", type: "error" });
      return;
    }

    if (!navigator.geolocation) {
      setMessage({ text: "Geolocation is required for manual attendance", type: "error" });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await markManualAttendance(targetSessionId, {
            student_id: selectedStudentId,
            coordinator_lat: pos.coords.latitude,
            coordinator_lon: pos.coords.longitude,
            location_accuracy: pos.coords.accuracy,
            reason: manualReason,
          });
          setMessage({ text: "Manual attendance marked successfully!", type: "success" });
          setSelectedStudentId("");
          fetchRecords(targetSessionId);
          fetchHistorySessions();
        } catch (err: any) {
          setMessage({ text: err.message || "Failed to mark manual attendance", type: "error" });
        }
      },
      (geoErr) => {
        setMessage({ text: "Coordinator GPS Location Error: " + geoErr.message, type: "error" });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleLogout = async () => {
    const ok = await logout();
    if (ok) {
      navigate("/");
    }
  };

  const filteredHistorySessions = historySessions.filter((s) => {
    const q = historySearchQuery.toLowerCase();
    return (
      (s.session_label && s.session_label.toLowerCase().includes(q)) ||
      (s.program_name && s.program_name.toLowerCase().includes(q)) ||
      (s.campus_location_name && s.campus_location_name.toLowerCase().includes(q)) ||
      (s.status && s.status.toLowerCase().includes(q))
    );
  });

  return (
    <div className="page12Container">
      <SidebarAdmin
        sidebarCollapsed={sidebarCollapsed}
        toggleSidebar={() => setSidebarCollapsed((prev) => !prev)}
        activeSection="dashboard"
      />

      <main className={`mainContent ${sidebarCollapsed ? "sidebarCollapsed" : ""}`}>
        <header className="contentHeader d-flex justify-content-between align-items-center">
          <h1 className="pageTitle">Attendance Management</h1>

          <div className="d-flex align-items-center gap-3">
            <div className="userProfile" style={{ position: "relative" }}>
              <div
                className="profileCircle"
                onClick={() => setShowDropdown((prev) => !prev)}
                style={{ cursor: "pointer" }}
              >
                <span className="profileInitial">A</span>
              </div>

              {showDropdown && (
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "45px",
                    background: "white",
                    borderRadius: "8px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                    padding: "10px",
                    minWidth: "130px",
                    zIndex: 100,
                  }}
                >
                  <button
                    onClick={handleLogout}
                    style={{
                      width: "100%",
                      background: "none",
                      border: "none",
                      textAlign: "left",
                      padding: "8px",
                      cursor: "pointer",
                      fontSize: "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="contentBody p-4">
          {message && (
            <div className={`alert ${message.type === "success" ? "alert-success" : "alert-danger"} alert-dismissible fade show`} role="alert">
              {message.type === "success" ? <CheckCircle2 className="me-2" size={18} /> : <AlertCircle className="me-2" size={18} />}
              {message.text}
              <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
            </div>
          )}

          {/* SECTION 1: CREATE SESSION FORM */}
          {!activeSessionId && (
            <section className="card shadow-sm border-0 mb-4 p-4">
              <h2 className="fs-5 fw-bold mb-3 text-primary d-flex align-items-center gap-2">
                <PlusCircle size={22} /> Prepare New Attendance Session
              </h2>

              <form onSubmit={handleCreateSession}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Select Course / Program *</label>
                    <select
                      className="form-select"
                      value={selectedProgramId}
                      onChange={(e) => setSelectedProgramId(e.target.value)}
                      required
                    >
                      <option value="">-- Choose Course --</option>
                      {programs.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.instructor_name || "Coordinator"})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Select Campus Location *</label>
                    <select
                      className="form-select"
                      value={selectedCampusLocationId}
                      onChange={(e) => setSelectedCampusLocationId(e.target.value)}
                      required
                    >
                      <option value="">-- Choose Campus Location --</option>
                      {filteredCampusLocations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name} (Radius: {loc.radius_meters || 100}m)
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Session Label *</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Week 4 - Algorithms Lecture"
                      value={sessionLabel}
                      onChange={(e) => setSessionLabel(e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Scheduled Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Scheduled Time</label>
                    <input
                      type="time"
                      className="form-control"
                      value={scheduledStartTime}
                      onChange={(e) => setScheduledStartTime(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mt-4 text-end">
                  <button type="submit" className="btn btn-primary px-4 d-inline-flex align-items-center gap-2">
                    <PlusCircle size={18} /> Prepare Session (SCHEDULED)
                  </button>
                </div>
              </form>
            </section>
          )}

          {/* SECTION 2: ACTIVE SESSION DASHBOARD (WITH QR & 6-DIGIT OTP) */}
          {activeSessionId && sessionData && (
            <section className="card shadow-sm border-0 mb-4 p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <span className={`badge ${sessionData.status === "ACTIVE" ? "bg-success" : sessionData.status === "SCHEDULED" ? "bg-warning text-dark" : "bg-secondary"} fs-6`}>
                    STATUS: {sessionData.status}
                  </span>
                  <h3 className="fs-4 fw-bold mt-2 mb-0">{sessionData.session_label}</h3>
                </div>

                <div className="d-flex gap-2">
                  {sessionData.status === "SCHEDULED" && (
                    <button className="btn btn-success d-flex align-items-center gap-2" onClick={() => handleStartSession(activeSessionId)}>
                      <Play size={18} /> Start Attendance (Enable QR / OTP)
                    </button>
                  )}

                  {sessionData.status === "ACTIVE" && (
                    <button className="btn btn-danger d-flex align-items-center gap-2" onClick={() => handleCloseSession(activeSessionId)}>
                      <Square size={18} /> Close Session (Disable QR / OTP)
                    </button>
                  )}

                  <button className="btn btn-outline-secondary" onClick={() => { setActiveSessionId(null); setDisplayData(null); if (timerRef.current) clearInterval(timerRef.current); }}>
                    Back / New Session
                  </button>
                </div>
              </div>

              {/* QR & 6-DIGIT OTP DISPLAY AREA */}
              {sessionData.status === "ACTIVE" && displayData && (
                <div className="row bg-light rounded p-4 align-items-center my-3">
                  <div className="col-md-6 text-center border-end">
                    <div className="d-flex justify-content-center align-items-center gap-4 flex-wrap">
                      {/* QR Code Container */}
                      <div className="bg-white p-3 rounded shadow-sm text-center">
                        <img src={`data:image/png;base64,${displayData.qr_image_base64}`} alt="Attendance QR Code" style={{ width: "210px", height: "210px" }} />
                        <small className="d-block text-muted mt-2 fw-semibold">Dynamic QR Code</small>
                      </div>

                      {/* 6-DIGIT OTP CONTAINER */}
                      <div className="bg-white p-4 rounded shadow-sm text-center d-flex flex-column justify-content-center align-items-center" style={{ minWidth: "210px", minHeight: "242px" }}>
                        <small className="text-primary fw-bold text-uppercase d-flex align-items-center gap-1 mb-2">
                          <Key size={18} /> 6-Digit OTP Code
                        </small>
                        <div className="display-4 fw-bold text-dark my-2 tracking-wider" style={{ letterSpacing: "6px", fontFamily: "monospace" }}>
                          {displayData.otp ? `${displayData.otp.slice(0, 3)} ${displayData.otp.slice(3)}` : "------"}
                        </div>
                        <span className="badge bg-primary text-white mt-2 px-3 py-2 fs-6">
                          Valid 10s
                        </span>
                      </div>
                    </div>

                    <small className="d-block text-muted mt-3">
                      Refreshing in <strong>{displayData.seconds_until_next_refresh}s</strong> (10-second rotating security window)
                    </small>
                  </div>

                  <div className="col-md-6">
                    <div className="card card-body border-0 shadow-sm mb-3">
                      <h6 className="text-muted fw-bold text-uppercase">Live Attendance Counter</h6>
                      <div className="display-4 fw-bold text-primary">{displayData.student_count || 0}</div>
                      <small className="text-secondary">Students Present</small>
                    </div>

                    <div className="alert alert-info mb-0">
                      <strong>Coordinator Instructions:</strong>
                      <ul className="mb-0 mt-1 ps-3">
                        <li>Project this display to your class.</li>
                        <li>Students can <strong>scan the QR code</strong> OR enter the <strong>6-digit OTP code</strong> on their device.</li>
                        <li>Both the QR and OTP rotate every 10 seconds to prevent unauthorized sharing.</li>
                        <li>Click <strong>Close Session</strong> when class attendance is complete.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* GEOFENCED MANUAL ATTENDANCE */}
              {sessionData.status === "ACTIVE" && (
                <div className="mt-4 pt-3 border-top">
                  <h4 className="fs-5 fw-bold mb-3 d-flex align-items-center gap-2">
                    <UserCheck size={20} className="text-warning" /> Coordinator Manual Attendance (Fallback)
                  </h4>

                  <form onSubmit={handleManualMarkSubmit} className="row g-3 align-items-end">
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Select Enrolled Student *</label>
                      <select className="form-select" value={selectedStudentId} onChange={(e) => setSelectedStudentId(e.target.value)} required>
                        <option value="">-- Choose Student --</option>
                        {enrolledStudents.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.roll_no} — {s.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-5">
                      <label className="form-label fw-semibold">Mandatory Reason *</label>
                      <select className="form-select" value={manualReason} onChange={(e) => setManualReason(e.target.value)} required>
                        <option value="QR scanner camera failed">QR scanner camera failed on student device</option>
                        <option value="Student device camera unavailable">Student device camera unavailable</option>
                        <option value="Student device network offline">Student device network offline</option>
                        <option value="Faculty physical verification">Faculty physical verification</option>
                        <option value="Other">Other verified exception</option>
                      </select>
                    </div>

                    <div className="col-md-3">
                      <button type="submit" className="btn btn-warning w-100 d-flex align-items-center justify-content-center gap-2">
                        <UserCheck size={18} /> Mark Present
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </section>
          )}

          {/* SECTION 3: SESSION HISTORY & TRACKING TABLE */}
          <section className="card shadow-sm border-0 mb-4 p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="fs-5 fw-bold mb-0 d-flex align-items-center gap-2 text-dark">
                <History size={22} className="text-primary" /> Session History & Past Sessions
              </h3>

              <div className="input-group" style={{ maxWidth: "300px" }}>
                <span className="input-group-text bg-white border-end-0">
                  <Search size={16} className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Filter sessions..."
                  value={historySearchQuery}
                  onChange={(e) => setHistorySearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Session Label</th>
                    <th>Course Name</th>
                    <th>Campus Location</th>
                    <th>Scheduled Date & Time</th>
                    <th>Status</th>
                    <th>Students Marked</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistorySessions.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-3">
                        No sessions recorded yet. Create your first session above.
                      </td>
                    </tr>
                  ) : (
                    filteredHistorySessions.map((s) => (
                      <tr key={s.id} className={selectedHistorySessionId === s.id ? "table-primary" : ""}>
                        <td className="fw-bold">{s.session_label}</td>
                        <td>{s.program_name}</td>
                        <td>{s.campus_location_name}</td>
                        <td>
                          {s.scheduled_date || "—"} {s.scheduled_start_time ? `@ ${s.scheduled_start_time}` : ""}
                        </td>
                        <td>
                          <span className={`badge ${s.status === "ACTIVE" ? "bg-success" : s.status === "SCHEDULED" ? "bg-warning text-dark" : "bg-secondary"}`}>
                            {s.status}
                          </span>
                        </td>
                        <td className="fw-bold text-primary">{s.student_count || 0} Present</td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-1">
                            <button
                              className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                              onClick={() => handleSelectHistorySession(s)}
                              title="View Marked Records"
                            >
                              <Eye size={15} /> Records
                            </button>

                            {s.status === "SCHEDULED" && (
                              <button className="btn btn-sm btn-success" onClick={() => handleStartSession(s.id)}>
                                Start
                              </button>
                            )}

                            {s.status === "ACTIVE" && (
                              <button className="btn btn-sm btn-danger" onClick={() => handleCloseSession(s.id)}>
                                Close
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* SECTION 4: MARKED ATTENDANCE RECORDS FOR SELECTED SESSION */}
          {selectedHistorySessionId && (
            <section className="card shadow-sm border-0 p-4">
              <h3 className="fs-5 fw-bold mb-3 d-flex align-items-center gap-2">
                <FileSpreadsheet size={22} className="text-success" /> Marked Attendance Records
              </h3>

              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Roll No</th>
                      <th>Student Name</th>
                      <th>Time Marked</th>
                      <th>Method</th>
                      <th>Location Actor</th>
                      <th>Reason (if manual)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-3">
                          No attendance records marked for this session.
                        </td>
                      </tr>
                    ) : (
                      records.map((r) => (
                        <tr key={r.id}>
                          <td className="fw-bold">{r.roll_no || "—"}</td>
                          <td>{r.student_name || r.student_id}</td>
                          <td>{new Date(r.marked_at).toLocaleTimeString()}</td>
                          <td>
                            <span className={`badge ${r.method === "QR" || r.method === "OTP" ? "bg-primary" : "bg-warning text-dark"}`}>
                              {r.method}
                            </span>
                          </td>
                          <td>{r.location_actor || "—"}</td>
                          <td>{r.manual_reason || "—"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
